/**
 * CATastrophe — 고양이 사진을 주는 곳(https://cata.smokat.cc).
 *
 * 프로필 사진을 올릴 사진이 없는 사람이 훨씬 많다. 이 앱 이름이 Cat Mail이니
 * 기본으로 내밀 그림도 고양이가 맞다.
 *
 * **브라우저가 직접 받아 온다.** 서버가 받아오게 하면 "우리 서버가 아무 주소나
 * 부르는 통로"가 생겨서(SSRF) 부를 수 있는 곳을 따로 못 박아야 하는데, 그러고도
 * 얻는 게 없다. 여기서 받아 오면 올린 사진과 **똑같은 길**을 탄다 — 자르고
 * 줄여서(lib/avatarImage.ts) 미리 보여주고, 사람이 확인한 뒤에 우리 서버에
 * 저장된다. 저장은 우리 쪽이라 저 서버가 죽어도 아바타는 안 깨진다.
 *
 * 저쪽이 CORS를 열어두었고(`Access-Control-Allow-Origin: *`) 이미지를 바이트로
 * 그대로 준다. 그래서 canvas가 오염되지 않는다 — fetch로 받은 바이트에는
 * 출처 딱지가 붙지 않는다.
 */

/** 기본은 운영 주소. 다른 데를 보게 하려면 `VITE_CAT_API`를 준다. */
const ORIGIN = import.meta.env.VITE_CAT_API ?? "https://cata.smokat.cc";

/** 고양이 한 마리. `image`는 저쪽 기준의 경로(`/cat/23`)다. */
type CatPick = { id: number; image: string };

export class CatProblem extends Error {}

/**
 * 고양이 한 마리를 고른다. 검색어가 비면 아무 고양이나.
 *
 * 같은 검색어로 다시 불러도 **다른 고양이가 온다** — 저쪽이 비슷한 것들 중에서
 * 골라 준다. 그래서 마음에 들 때까지 다시 누르는 것이 성립한다.
 */
export async function pickCat(query: string): Promise<Blob> {
  const trimmed = query.trim();
  const path = trimmed ? `/cat?q=${encodeURIComponent(trimmed)}` : "/cat";

  const picked = await get(path);
  const chosen = (await picked.json()) as Partial<CatPick>;
  if (!chosen.image) throw new CatProblem("고양이를 찾지 못했어요");

  const image = await get(chosen.image);
  const blob = await image.blob();
  if (!blob.type.startsWith("image/")) {
    throw new CatProblem("고양이를 데려오지 못했어요");
  }

  return blob;
}

async function get(path: string): Promise<Response> {
  let response: Response;
  try {
    response = await fetch(`${ORIGIN}${path}`, {
      // 남의 서버다. 우리 쿠키가 따라갈 이유가 없다.
      credentials: "omit",
      signal: AbortSignal.timeout(10_000),
    });
  } catch {
    throw new CatProblem("고양이를 데려오지 못했어요. 잠시 뒤에 다시 해보세요");
  }

  if (!response.ok) throw new CatProblem("고양이를 데려오지 못했어요");
  return response;
}
